<template>
  <div class="pa-account">
    <v-card class="white-common-box pa-account__card">
      <div class="pa-account__head">
        <div>
          <div class="pa-account__title">POP Authority packages</div>
          <div class="pa-account__sub">One monthly package per project. Manage, change or cancel each from its project page.</div>
        </div>
        <div v-if="eligibleSites.length" class="pa-account__subscribe">
          <select v-model="pickedSiteId" class="pa-sel pa-sel--inline">
            <option :value="null" disabled>Choose a project</option>
            <option v-for="s in eligibleSites" :key="s.id" :value="s.id">{{ s.name }}</option>
          </select>
          <button type="button" class="pa-btn pa-btn--orange" :disabled="!pickedSiteId" @click="goToPackages(pickedSiteId)">
            Subscribe a project
          </button>
        </div>
      </div>

      <div v-if="loading" class="pa-account__loading">Loading…</div>
      <div v-else-if="loadError" class="pa-err">We could not load your packages. Try again in a moment.</div>

      <div v-else-if="!subscriptions.length" class="pa-account__empty">
        <i class="material-icons" aria-hidden="true">workspace_premium</i>
        <div>
          <b>No package yet.</b>
          Packages open for a project once its audit is delivered and reviewed with your SEO Manager.
          <template v-if="eligibleSites.length">Pick a project above to get started.</template>
        </div>
      </div>

      <div v-else class="pa-account__table-wrap">
        <table class="pa-account__table">
          <thead>
            <tr>
              <th>Project</th>
              <th>Package</th>
              <th>Status</th>
              <th>Progress</th>
              <th>{{ anyInvoiceSent ? 'Next invoice / due' : 'Next invoice' }}</th>
              <th class="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="sub in subscriptions" :key="sub.id">
              <td>
                <router-link :to="{ name: 'OverviewWhiteGlove', params: { siteId: String(sub.siteId) } }" class="pa-account__site">
                  {{ sub.siteName || ('Project #' + sub.siteId) }}
                </router-link>
              </td>
              <td>
                <b>{{ sub.package && sub.package.name }}</b>
                <div class="pa-account__muted">${{ money(sub.package && sub.package.price) }}/mo</div>
              </td>
              <td><span class="pa-status pa-status--pill" :class="statusClass(sub)">{{ statusLabel(sub) }}</span></td>
              <td>{{ progressLabel(sub) }}</td>
              <td>{{ dateLabel(sub) }}</td>
              <td class="text-right pa-account__actions">
                <a
                  v-if="sub.status === 'invoice_sent' && sub.stripeHostedInvoiceUrl"
                  class="pa-link"
                  :href="sub.stripeHostedInvoiceUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                >Pay invoice</a>
                <button type="button" class="pa-link" @click="goToPackages(sub.siteId)">
                  {{ isLive(sub) ? 'Manage package' : 'View packages' }}
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </v-card>
  </div>
</template>

<script>
import {
  POP_AUTHORITY_LIVE_SUBSCRIPTION_STATUSES,
  popAuthorityCampaignProgressLabel,
  popAuthorityMoney,
  popAuthoritySubscriptionStatusLabel,
} from '@/utils/popAuthority'

function parseApiDate (value) {
  if (!value) return null
  const m = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}):(\d{2}))?/.exec(String(value))
  if (!m) return null
  return new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], +(m[4] || 0), +(m[5] || 0), +(m[6] || 0)))
}

/** Account settings > Billing and payments > POP Authority packages: every project's subscription. */
export default {
  name: 'PopAuthorityAccountPackages',
  data () {
    return {
      loading: true,
      loadError: false,
      subscriptions: [],
      eligibleSites: [],
      pickedSiteId: null,
    }
  },
  computed: {
    anyInvoiceSent () {
      return this.subscriptions.some((s) => s.status === 'invoice_sent')
    },
  },
  mounted () {
    this.load()
  },
  methods: {
    // package prices can carry cents (Authority Custom mixes land on .50)
    money: popAuthorityMoney,
    isLive (sub) {
      return POP_AUTHORITY_LIVE_SUBSCRIPTION_STATUSES.includes(sub.status)
    },
    statusLabel: popAuthoritySubscriptionStatusLabel,
    statusClass (sub) {
      if (sub.status === 'active' && !sub.cancelAtPeriodEnd) return 'pa-status--green'
      if (['canceled', 'checkout_pending', 'paused'].includes(sub.status)) return 'pa-status--gray'
      return 'pa-status--orange'
    },
    progressLabel (sub) {
      if (!this.isLive(sub)) return '—'
      return popAuthorityCampaignProgressLabel(sub.currentCycle, sub.cycleAnchorAt)
    },
    dateLabel (sub) {
      let value = null
      let prefix = ''
      if (sub.status === 'invoice_sent') {
        const sent = parseApiDate(sub.invoiceSentAt)
        if (sent) {
          value = new Date(sent.getTime() + 7 * 24 * 3600 * 1000)
          prefix = 'Due '
        }
      } else if (sub.cancelAtPeriodEnd) {
        value = parseApiDate(sub.currentPeriodEnd)
        prefix = 'Ends '
      } else if (this.isLive(sub)) {
        value = parseApiDate(sub.nextInvoiceAt)
      }
      if (!value) return '—'
      return prefix + value.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    },
    goToPackages (siteId) {
      if (!siteId) return
      this.$router.push({ name: 'PopAuthorityPackages', params: { siteId: String(siteId) }, query: { from: 'account-settings' } })
    },
    load () {
      this.loading = true
      this.loadError = false
      return this.$store
        .dispatch('getPopAuthoritySubscriptions')
        .then((response) => {
          const data = response && response.data
          if (!data || data.status !== 'SUCCESS') {
            this.loadError = true
            return
          }
          this.subscriptions = data.subscriptions || []
          this.eligibleSites = data.eligibleSites || []
          if (this.eligibleSites.length === 1) this.pickedSiteId = this.eligibleSites[0].id
        })
        .catch(() => {
          this.loadError = true
        })
        .finally(() => {
          this.loading = false
        })
    },
  },
}
</script>

<style lang="scss" scoped>
@import './setup/setup.scss';

.pa-account {
  color: var(--v-mainColor-base);
  font-size: 0.9rem;

  &__card {
    padding: 20px 22px;
  }

  &__head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    flex-wrap: wrap;
    margin-bottom: 16px;
  }

  &__title {
    font-size: 1.1rem;
    font-weight: 800;
  }

  &__sub {
    font-size: 0.85rem;
    color: var(--v-gray2Color-base, #757575);
  }

  &__subscribe {
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;
  }

  &__loading {
    margin: 10px 0;
  }

  &__empty {
    display: flex;
    gap: 12px;
    align-items: flex-start;
    background: var(--v-pink1Color-base, #f7f5fb);
    border-radius: 10px;
    padding: 14px 16px;
    font-size: 0.9rem;

    .material-icons {
      color: var(--v-profileAnchorColor-base);
      font-size: 26px;
    }
  }

  &__table-wrap {
    overflow-x: auto;
  }

  &__table {
    width: 100%;
    border-collapse: collapse;

    th,
    td {
      padding: 10px 10px;
      border-bottom: 1px solid var(--v-gray15Color-base, #e4e4e4);
      text-align: left;
      vertical-align: middle;
      font-size: 0.88rem;
    }

    th {
      font-size: 0.78rem;
      text-transform: uppercase;
      letter-spacing: 0.03em;
      color: var(--v-gray2Color-base, #757575);
    }

    .text-right {
      text-align: right;
    }
  }

  &__site {
    font-weight: 700;
    color: var(--v-mainColor-base);
    text-decoration: none;

    &:hover {
      color: var(--v-profileAnchorColor-base);
    }
  }

  &__muted {
    font-size: 0.78rem;
    color: var(--v-gray2Color-base, #757575);
  }

  &__actions {
    display: flex;
    gap: 12px;
    justify-content: flex-end;
    align-items: center;
  }
}

.pa-status--gray {
  background: var(--v-gray7Color-base, #f7f7f7);
  color: var(--v-gray2Color-base, #757575);
}
</style>
